import { useState } from "react";
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import theme from '../../theme';

import Authentication from "./Authentication";
import ProgressIndicator from "./ProgressIndicator";
import PersonalDetailsForm from "./PersonalDetailsForm";
import MaritalStatusForm from "./MaritalStatusForm";
import ExecutorDetailsForm from "./ExecutorDetailsForm";
import ChildrenTrustForm from "./ChildrenTrustForm";
import EstateDisposalForm from "./EstateDisposalForm";
import WillDocumentDisplay from "./WillDocumentDisplay";
import { AppContainer } from './WillGenerator.styles';

export default function WillGenerator() {
	const [isAuthenticated, setIsAuthenticated] = useState(false);

	const [page, setPage] = useState(1);
	const [formData, setFormData] = useState({
		// Personal Details
		title: "",
		firstName: "",
		lastName: "",
		email: "",
		cellphone: "",
		idNumber: "", // Will be pre-filled after authentication
		address: "",
		country: "South Africa",
		postalCode: "",

		// Marital Status
		maritalStatus: "",
		marriageType: "",
		includeExSpouse: false,
		allocateInheritance: false,
		multipleSpouses: false,
		legalMarriage: false,
		spouseTitle: "",
		spouseFirstName: "",
		spouseLastName: "",
		spouseIdNumber: "",
		divorceOrderRegistered: false,
		dateOfDeathSpouse: null,

		// Executors
		executor1FirstName: "",
		executor1LastName: "",
		executor1IdNumber: "",
		executor2FirstName: "",
		executor2LastName: "",
		executor2IdNumber: "",

		// Children's Trust
		hasChildren: false,
		hasMinorChildren: false,
		children: [],
		executorsAsTrustees: true,
		trustee: { firstName: "", lastName: "", idNumber: "" },

		// Page 5: Disposal of Estate
		estateItems: [],
		remainderBeneficiaries: [
		],

		// New beneficiaries management
		customBeneficiaries: [],
	});

	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleAuthenticationSuccess = (validatedIdNumber) => {
		setIsAuthenticated(true);
		setFormData(prev => ({
			...prev,
			idNumber: validatedIdNumber,
		}));
	};

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	const handleRadioChange = (name, value) => {
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsSubmitting(true);

		if (page === 5) {
			const totalPercentage = formData.remainderBeneficiaries.reduce(
				(sum, ben) => sum + (parseInt(ben.percentage, 10) || 0),
				0
			);

			if (formData.remainderBeneficiaries.length > 0 && totalPercentage !== 100) {
				alert(`Total remainder percentage must equal 100%. Current total: ${totalPercentage}%`);
				setIsSubmitting(false);
				return;
			}
		}

		await new Promise(resolve => setTimeout(resolve, 500));

		setPage(currentPage => currentPage + 1);
		window.scrollTo(0, 0);
		setIsSubmitting(false);
	};

	const handleBack = () => {
		setPage(currentPage => currentPage - 1);
		window.scrollTo(0, 0);
	};

	const addEstateItem = (type) => {
		const newItemId = Date.now();
		let newItem = { id: newItemId, type: type };

		switch (type) {
			case "immovable":
				newItem = { ...newItem, address: "", beneficiaries: "", hasLifelongUsufruct: false, usufructBeneficiary: "", specialConditions: "" }; break;
			case "movable":
				newItem = { ...newItem, description: "", beneficiaries: "" }; break;
			case "cash":
				newItem = { ...newItem, amount: "", amountInWords: "", beneficiary: "", purpose: "", alternativeBeneficiary: "" }; break;
			case "business":
				newItem = { ...newItem, companyName: "", registrationNumber: "", sharePercentage: "", hasAgreement: false, beneficiary: "" }; break;
			case "trust":
				newItem = { ...newItem, trustType: "", trustName: "", beneficiaries: "", conditions: "" }; break;
			case "digital":
				newItem = { ...newItem, assetType: "", description: "", accessInformation: "", beneficiary: "" }; break;
			case "personal":
				newItem = { ...newItem, itemDescription: "", sentimentalValue: "", beneficiary: "" }; break;
			case "policy":
				newItem = { ...newItem, policyType: "", policyNumber: "", provider: "", accountNumber: "" }; break;
			default: break;
		}

		setFormData((prevData) => ({
			...prevData,
			estateItems: [...prevData.estateItems, newItem],
		}));
	};

	const updateEstateItem = (itemId, field, value) => {
		setFormData((prevData) => ({
			...prevData,
			estateItems: prevData.estateItems.map((item) =>
				item.id === itemId ? { ...item, [field]: value } : item,
			),
		}));
	};

	const removeEstateItem = (itemId) => {
		setFormData((prevData) => ({
			...prevData,
			estateItems: prevData.estateItems.filter((item) => item.id !== itemId),
		}));
	};

	const addRemainderBeneficiary = () => {
		const newId = Date.now();
		setFormData((prevData) => ({
			...prevData,
			remainderBeneficiaries: [
				...prevData.remainderBeneficiaries,
				{ id: newId, name: "", relationship: "", percentage: "" },
			],
		}));
	};

	const updateRemainderBeneficiary = (beneficiaryId, field, value) => {
		setFormData((prevData) => ({
			...prevData,
			remainderBeneficiaries: prevData.remainderBeneficiaries.map((ben) =>
				ben.id === beneficiaryId ? { ...ben, [field]: value } : ben,
			),
		}));
	};

	const removeRemainderBeneficiary = (beneficiaryId) => {
		setFormData((prevData) => ({
			...prevData,
			remainderBeneficiaries: prevData.remainderBeneficiaries.filter(
				(ben) => ben.id !== beneficiaryId,
			),
		}));
	};

	const handleBeneficiarySelect = (itemId, type, selectedValue, fieldToUpdate = 'name') => {
		if (selectedValue === "other") {
			if (type === "estateItem") {
				updateEstateItem(itemId, fieldToUpdate, "");
			} else if (type === "remainder") {
				updateRemainderBeneficiary(itemId, "name", "");
			}
		} else {
			const selectedBeneficiary = getPotentialBeneficiaries().find(b => b.id === selectedValue);

			if (selectedBeneficiary) {
				if (type === "estateItem") {
					updateEstateItem(itemId, fieldToUpdate, selectedBeneficiary.name);
				} else if (type === "remainder") {
					updateRemainderBeneficiary(itemId, "name", selectedBeneficiary.name);
					const currentRelationship = formData.remainderBeneficiaries.find(b => b.id === itemId)?.relationship;
					if (!currentRelationship && selectedBeneficiary.relationship && !['Testator/Testatrix', 'Executor'].includes(selectedBeneficiary.relationship)) {
						updateRemainderBeneficiary(itemId, "relationship", selectedBeneficiary.relationship);
					}
				}
			} else {
				console.warn("Selected beneficiary not found:", selectedValue);
			}
		}
	};

	const addChild = () => {
		const newId = Date.now();
		setFormData((prevData) => ({
			...prevData,
			children: [
				...prevData.children,
				{ id: newId, name: "", dateOfBirth: null, age: null, isMinor: false },
			],
		}));
	};

	const updateChild = (childId, field, value) => {
		setFormData((prevData) => ({
			...prevData,
			children: prevData.children.map((child) =>
				child.id === childId ? { ...child, [field]: value } : child,
			),
		}));
	};

	const removeChild = (childId) => {
		setFormData((prevData) => {
			const updatedChildren = prevData.children.filter((child) => child.id !== childId);
			const hasMinors = updatedChildren.some(child => child.isMinor);
			return {
				...prevData,
				children: updatedChildren,
				hasMinorChildren: hasMinors,
			};
		});
	};

	const calculateAge = (dateOfBirth) => {
		if (!dateOfBirth || !(dateOfBirth instanceof Date) || isNaN(dateOfBirth.getTime())) {
			return null;
		}
		const today = new Date();
		const birthDate = new Date(dateOfBirth.getFullYear(), dateOfBirth.getMonth(), dateOfBirth.getDate());
		let age = today.getFullYear() - birthDate.getFullYear();
		const m = today.getMonth() - birthDate.getMonth();
		if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
			age--;
		}
		return age >= 0 ? age : null;
	};

	const handleChildDateChange = (childId, date) => {
		const age = calculateAge(date);
		const isMinor = age !== null && age < 18;

		setFormData((prevData) => {
			const updatedChildren = prevData.children.map((child) =>
				child.id === childId ? { ...child, dateOfBirth: date, age: age, isMinor: isMinor } : child
			);
			const hasMinors = updatedChildren.some(child => child.isMinor);
			return {
				...prevData,
				children: updatedChildren,
				hasMinorChildren: hasMinors,
			};
		});
	};

	const getPotentialBeneficiaries = () => {
		const beneficiaries = [];

		if (formData.firstName && formData.lastName) {
			beneficiaries.push({
				id: 'testator',
				name: `${formData.firstName} ${formData.lastName}`,
				relationship: 'Testator/Testatrix',
				idNumber: formData.idNumber || ''
			});
		}
		if (formData.maritalStatus === 'married' && formData.spouseFirstName && formData.spouseLastName) {
			beneficiaries.push({
				id: 'spouse',
				name: `${formData.spouseFirstName} ${formData.spouseLastName}`,
				relationship: 'Spouse',
				idNumber: formData.spouseIdNumber || ''
			});
		}
		formData.children.forEach(child => {
			if (child.name) {
				beneficiaries.push({
					id: `child_${child.id}`,
					name: child.name,
					relationship: 'Child',
					idNumber: ''
				});
			}
		});
		if (formData.executor1FirstName && formData.executor1LastName) {
			beneficiaries.push({
				id: 'executor1',
				name: `${formData.executor1FirstName} ${formData.executor1LastName}`,
				relationship: 'Executor',
				idNumber: formData.executor1IdNumber || ''
			});
		}
		if (formData.executor2FirstName && formData.executor2LastName) {
			beneficiaries.push({
				id: 'executor2',
				name: `${formData.executor2FirstName} ${formData.executor2LastName}`,
				relationship: 'Executor',
				idNumber: formData.executor2IdNumber || ''
			});
		}
		if (!formData.executorsAsTrustees && formData.trustee.firstName && formData.trustee.lastName) {
			beneficiaries.push({
				id: 'trustee',
				name: `${formData.trustee.firstName} ${formData.trustee.lastName}`,
				relationship: 'Trustee',
				idNumber: formData.trustee.idNumber || ''
			});
		}
		formData.customBeneficiaries.forEach(ben => {
			beneficiaries.push({
				id: `custom_${ben.id}`,
				name: `${ben.firstName} ${ben.lastName}`,
				relationship: ben.relationship || 'Other',
				idNumber: ben.idNumber || ''
			});
		});

		const uniqueBeneficiaries = beneficiaries.filter((ben, index, self) =>
			index === self.findIndex((b) => (
				b.name === ben.name && b.relationship === ben.relationship
			))
		);

		return uniqueBeneficiaries;
	};

	if (!isAuthenticated) {
		return (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<ThemeProvider theme={theme}>
					<CssBaseline />
					<Authentication onAuthenticationSuccess={handleAuthenticationSuccess} />
				</ThemeProvider>
			</LocalizationProvider>
		);
	}

	const steps = ["Personal Details", "Marital Status", "Executors", "Children", "Estate"];

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<AppContainer maxWidth="md">
					{page <= 5 && <ProgressIndicator currentPage={page} totalSteps={steps.length} stepTitles={steps} />}

					{page === 1 && (
						<PersonalDetailsForm
							formData={formData}
							handleChange={handleChange}
							handleSubmit={handleSubmit}
							isSubmitting={isSubmitting}
							setFormData={setFormData}
						/>
					)}

					{page === 2 && (
						<MaritalStatusForm
							formData={formData}
							handleChange={handleChange}
							handleRadioChange={handleRadioChange}
							handleSubmit={handleSubmit}
							handleBack={handleBack}
							isSubmitting={isSubmitting}
							setFormData={setFormData}
						/>
					)}

					{page === 3 && (
						<ExecutorDetailsForm
							formData={formData}
							handleChange={handleChange}
							handleSubmit={handleSubmit}
							handleBack={handleBack}
							isSubmitting={isSubmitting}
						/>
					)}

					{page === 4 && (
						<ChildrenTrustForm
							formData={formData}
							handleChange={handleChange}
							handleRadioChange={handleRadioChange}
							handleSubmit={handleSubmit}
							handleBack={handleBack}
							isSubmitting={isSubmitting}
							setFormData={setFormData}
							addChild={addChild}
							updateChild={updateChild}
							handleChildDateChange={handleChildDateChange}
							removeChild={removeChild}
						/>
					)}

					{page === 5 && (
						<EstateDisposalForm
							formData={formData}
							handleSubmit={handleSubmit}
							handleBack={handleBack}
							isSubmitting={isSubmitting}
							addEstateItem={addEstateItem}
							updateEstateItem={updateEstateItem}
							removeEstateItem={removeEstateItem}
							addRemainderBeneficiary={addRemainderBeneficiary}
							updateRemainderBeneficiary={updateRemainderBeneficiary}
							removeRemainderBeneficiary={removeRemainderBeneficiary}
							handleBeneficiarySelect={handleBeneficiarySelect}
							getPotentialBeneficiaries={getPotentialBeneficiaries}
							setFormData={setFormData}
						/>
					)}

					{page === 6 && (
						<WillDocumentDisplay
							formData={formData}
							handleBack={handleBack}
						/>
					)}


				</AppContainer>
			</ThemeProvider>
		</LocalizationProvider>
	);
}

